import React from 'react';
import { DEMO_EVIDENCE_EVENTS, DEMO_PATIENTS, SITE_MAP } from '../api/demoData';

// ---------- shared styles ----------

const cardStyle: React.CSSProperties = {
  background: '#1a202c',
  border: '1px solid #2d3748',
  borderRadius: 8,
  padding: 16,
  marginBottom: 16,
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 12px',
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: '#a0aec0',
  borderBottom: '1px solid #2d3748',
};

const tdStyle: React.CSSProperties = {
  padding: '8px 12px',
  fontSize: 13,
  color: '#e2e8f0',
  borderBottom: '1px solid #2d3748',
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
};

const badgeBase: React.CSSProperties = {
  borderRadius: 12,
  padding: '2px 8px',
  fontSize: 11,
  fontWeight: 600,
  display: 'inline-block',
};

const headingStyle: React.CSSProperties = {
  color: '#e2e8f0',
  fontSize: 16,
  fontWeight: 600,
  marginBottom: 12,
};

const subHeadingStyle: React.CSSProperties = {
  color: '#e2e8f0',
  fontSize: 14,
  fontWeight: 600,
  marginBottom: 8,
};

const mutedText: React.CSSProperties = {
  color: '#a0aec0',
  fontSize: 12,
};

// ---------- monitor evidence data ----------

type VerificationStatus = 'Verified' | 'Pending' | 'Discrepancy';
type QueryStatus = 'Open' | 'Closed' | 'None';
type Source = 'UCLA Epic' | 'UCSF Epic' | 'JH Epic' | 'Paper CRF' | 'Direct Entry';

interface MonitorEvidenceRow {
  id: string;
  eventType: string;
  patientId: string;
  verified: VerificationStatus;
  source: Source;
  query: QueryStatus;
}

const patientIdFromSubject = (subject: string): string => subject.replace('Patient/', '');

const sourceForPatient = (pid: string): Source => {
  const site = SITE_MAP[pid];
  if (!site) return 'Direct Entry';
  if (site.label === 'UCLA') return 'UCLA Epic';
  if (site.label === 'UCSF') return 'UCSF Epic';
  if (site.label === 'Johns Hopkins') return 'JH Epic';
  return 'Direct Entry';
};

const monitorRows: MonitorEvidenceRow[] = DEMO_EVIDENCE_EVENTS.map((ev) => {
  const pid = patientIdFromSubject(ev.subject);
  let verified: VerificationStatus = 'Verified';
  let query: QueryStatus = 'None';

  if (ev.id === 'ev-007') {
    verified = 'Pending';
    query = 'Open';
  } else if (ev.id === 'ev-013') {
    verified = 'Pending';
    query = 'None';
  } else if (ev.id === 'ev-019') {
    verified = 'Discrepancy';
    query = 'Open';
  }

  return {
    id: ev.id,
    eventType: ev.eventType,
    patientId: pid,
    verified,
    source: sourceForPatient(pid),
    query,
  };
});

// sort: unverified first
const sortedMonitorRows = [...monitorRows].sort((a, b) => {
  const order: Record<VerificationStatus, number> = { Discrepancy: 0, Pending: 1, Verified: 2 };
  return order[a.verified] - order[b.verified];
});

const verifiedCount = monitorRows.filter((r) => r.verified === 'Verified').length;
const openQueries = monitorRows.filter((r) => r.query === 'Open').length;

// ---------- 1. EvidenceMonitorPanel ----------

export const EvidenceMonitorPanel: React.FC = () => {
  const renderVerified = (status: VerificationStatus) => {
    if (status === 'Verified') return <span style={{ color: '#48bb78', fontSize: 16 }}>&#10003;</span>;
    if (status === 'Pending') return <span style={{ color: '#ecc94b', fontSize: 16 }}>&#9201;</span>;
    return <span style={{ color: '#f56565', fontSize: 16 }}>&#10007;</span>;
  };

  const renderQuery = (q: QueryStatus) => {
    if (q === 'Open')
      return <span style={{ ...badgeBase, background: '#ecc94b', color: '#1a202c' }}>Open</span>;
    if (q === 'Closed')
      return <span style={{ ...badgeBase, background: '#48bb78', color: '#1a202c' }}>Closed</span>;
    return <span style={{ ...badgeBase, background: '#2d3748', color: '#a0aec0' }}>None</span>;
  };

  return (
    <div style={cardStyle}>
      <h3 style={headingStyle}>Evidence Monitor Panel</h3>
      <p style={{ ...mutedText, marginBottom: 12 }}>
        SDV Progress: {verifiedCount}/{monitorRows.length} verified (
        {Math.round((verifiedCount / monitorRows.length) * 100)}%) &mdash; {openQueries} queries open
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>ID</th>
              <th style={thStyle}>Type</th>
              <th style={thStyle}>Patient</th>
              <th style={thStyle}>Verified</th>
              <th style={thStyle}>Source</th>
              <th style={thStyle}>Query</th>
            </tr>
          </thead>
          <tbody>
            {sortedMonitorRows.map((row) => (
              <tr key={row.id}>
                <td style={tdStyle}>{row.id}</td>
                <td style={tdStyle}>{row.eventType}</td>
                <td style={tdStyle}>{row.patientId}</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>{renderVerified(row.verified)}</td>
                <td style={tdStyle}>{row.source}</td>
                <td style={tdStyle}>{renderQuery(row.query)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------- 2. SafetyMonitorPanel ----------

const cohortSdv: { cohort: string; rate: number }[] = [
  { cohort: 'Cohort 1 (10mg)', rate: 95 },
  { cohort: 'Cohort 2 (25mg)', rate: 78 },
  { cohort: 'Cohort 3 (50mg)', rate: 71 },
];

const outstandingQueryPatients = [
  { patientId: 'SYN-004', queries: 1 },
  { patientId: 'SYN-007', queries: 1 },
];

const monitorVisits = [
  { site: 'UCLA', last: '2026-01-28', next: '2026-02-04' },
  { site: 'UCSF', last: '2026-01-25', next: '2026-02-01' },
  { site: 'Johns Hopkins', last: '2026-01-27', next: '2026-02-03' },
];

export const SafetyMonitorPanel: React.FC = () => {
  const rateColor = (rate: number) => {
    if (rate >= 90) return '#48bb78';
    if (rate >= 75) return '#ecc94b';
    return '#f56565';
  };

  return (
    <div style={cardStyle}>
      <h3 style={headingStyle}>Safety Monitor Panel</h3>

      {/* SDV per cohort */}
      <div style={{ marginBottom: 16 }}>
        <h4 style={subHeadingStyle}>SDV Completion by Cohort</h4>
        {cohortSdv.map((c) => (
          <div key={c.cohort} style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ color: '#e2e8f0', fontSize: 13, width: 160 }}>{c.cohort}</span>
            <div
              style={{
                flex: 1,
                height: 8,
                background: '#2d3748',
                borderRadius: 4,
                marginRight: 8,
              }}
            >
              <div
                style={{
                  width: `${c.rate}%`,
                  height: 8,
                  background: rateColor(c.rate),
                  borderRadius: 4,
                }}
              />
            </div>
            <span style={{ color: rateColor(c.rate), fontSize: 13, fontWeight: 600, width: 40, textAlign: 'right' }}>
              {c.rate}%
            </span>
          </div>
        ))}
      </div>

      {/* Outstanding queries */}
      <div style={{ marginBottom: 16 }}>
        <h4 style={subHeadingStyle}>Patients with Outstanding Queries</h4>
        {outstandingQueryPatients.map((p) => (
          <div
            key={p.patientId}
            style={{
              display: 'inline-block',
              ...badgeBase,
              background: 'rgba(236,201,75,0.15)',
              color: '#ecc94b',
              marginRight: 8,
              padding: '4px 12px',
            }}
          >
            {p.patientId} &mdash; {p.queries} query
          </div>
        ))}
      </div>

      {/* Monitor visits */}
      <div>
        <h4 style={subHeadingStyle}>Monitor Visit Schedule</h4>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Site</th>
              <th style={thStyle}>Last Visit</th>
              <th style={thStyle}>Next Scheduled</th>
            </tr>
          </thead>
          <tbody>
            {monitorVisits.map((v) => (
              <tr key={v.site}>
                <td style={tdStyle}>{v.site}</td>
                <td style={tdStyle}>{v.last}</td>
                <td style={tdStyle}>{v.next}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------- 3. PatientMonitorPanel ----------

type DataSource = 'EDC Entry' | 'Direct Observation' | 'Patient Report' | 'Lab System';
type CustodyStatus = 'Complete' | 'Partial' | 'Missing';

interface PatientEventDetail {
  eventId: string;
  eventType: string;
  dataSource: DataSource;
  custodyStatus: CustodyStatus;
  queryOpen: boolean;
}

const dataSourceByType: Record<string, DataSource> = {
  'Vital Signs': 'Direct Observation',
  'Lab Result': 'Lab System',
  'AE Report': 'EDC Entry',
  'SAE Report': 'EDC Entry',
  'PK Sample': 'Lab System',
  ECG: 'Direct Observation',
};

const buildPatientEvents = (patientId: string): PatientEventDetail[] => {
  const events = DEMO_EVIDENCE_EVENTS.filter(
    (ev) => patientIdFromSubject(ev.subject) === patientId
  );
  return events.map((ev) => {
    const row = monitorRows.find((r) => r.id === ev.id);
    let custodyStatus: CustodyStatus = 'Complete';
    if (row && row.verified === 'Pending') custodyStatus = 'Partial';
    if (row && row.verified === 'Discrepancy') custodyStatus = 'Missing';

    return {
      eventId: ev.id,
      eventType: ev.eventType,
      dataSource: dataSourceByType[ev.eventType] || 'Patient Report',
      custodyStatus,
      queryOpen: row ? row.query === 'Open' : false,
    };
  });
};

export const PatientMonitorPanel: React.FC<{ patientId: string }> = ({ patientId }) => {
  const events = buildPatientEvents(patientId);
  const patient = DEMO_PATIENTS.find((p) => p.id === patientId);
  const openQueryCount = events.filter((e) => e.queryOpen).length;

  const custodyColor = (s: CustodyStatus) => {
    if (s === 'Complete') return '#48bb78';
    if (s === 'Partial') return '#ecc94b';
    return '#f56565';
  };

  return (
    <div style={cardStyle}>
      <h3 style={headingStyle}>Patient Monitor Panel &mdash; {patientId}</h3>

      {/* Consent compliance */}
      <div style={{ marginBottom: 12 }}>
        <span style={mutedText}>Consent Version:</span>{' '}
        <span style={{ ...badgeBase, background: '#48bb78', color: '#1a202c' }}>v2.1 &mdash; current</span>
      </div>

      {/* Outstanding queries */}
      <div style={{ marginBottom: 12 }}>
        <span style={mutedText}>Outstanding Queries:</span>{' '}
        <span style={{ color: openQueryCount > 0 ? '#ecc94b' : '#48bb78', fontSize: 13, fontWeight: 600 }}>
          {openQueryCount}
        </span>
      </div>

      {/* Cohort info */}
      {patient && (
        <div style={{ marginBottom: 12 }}>
          <span style={mutedText}>Cohort:</span>{' '}
          <span style={{ color: '#e2e8f0', fontSize: 13 }}>{patient.cohort}</span>
        </div>
      )}

      {/* Event table */}
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Event ID</th>
            <th style={thStyle}>Type</th>
            <th style={thStyle}>Data Source</th>
            <th style={thStyle}>Chain of Custody</th>
            <th style={thStyle}>Query</th>
          </tr>
        </thead>
        <tbody>
          {events.map((ev) => (
            <tr key={ev.eventId}>
              <td style={tdStyle}>{ev.eventId}</td>
              <td style={tdStyle}>{ev.eventType}</td>
              <td style={tdStyle}>{ev.dataSource}</td>
              <td style={tdStyle}>
                <span style={{ ...badgeBase, background: custodyColor(ev.custodyStatus), color: '#1a202c' }}>
                  {ev.custodyStatus}
                </span>
              </td>
              <td style={tdStyle}>
                {ev.queryOpen ? (
                  <span style={{ ...badgeBase, background: '#ecc94b', color: '#1a202c' }}>Open</span>
                ) : (
                  <span style={{ ...badgeBase, background: '#2d3748', color: '#a0aec0' }}>None</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ---------- 4. DeviationMonitorPanel ----------

type DetectionMethod = 'Self-Reported' | 'Monitor-Detected';
type CorrectiveAction = 'Pending' | 'In Progress' | 'Complete';
type DataIntegrityImpact = 'None' | 'Minor' | 'Major';

interface DeviationMonitorRow {
  id: string;
  patientId: string;
  visit: number;
  type: string;
  description: string;
  detection: DetectionMethod;
  correctiveAction: CorrectiveAction;
  dataImpact: DataIntegrityImpact;
}

const deviationMonitorData: DeviationMonitorRow[] = [
  {
    id: 'pd-001',
    patientId: 'SYN-002',
    visit: 2,
    type: 'Major',
    description: 'PK sample taken 3h late at visit 2 (should be 1h post-dose)',
    detection: 'Monitor-Detected',
    correctiveAction: 'Complete',
    dataImpact: 'Major',
  },
  {
    id: 'pd-002',
    patientId: 'SYN-005',
    visit: 1,
    type: 'Major',
    description: 'Patient received wrong dose level at visit 1 -- 25mg instead of 10mg',
    detection: 'Monitor-Detected',
    correctiveAction: 'In Progress',
    dataImpact: 'Major',
  },
  {
    id: 'pd-003',
    patientId: 'SYN-001',
    visit: 1,
    type: 'Minor',
    description: 'Vital signs recorded 2.5h after collection',
    detection: 'Self-Reported',
    correctiveAction: 'Complete',
    dataImpact: 'Minor',
  },
  {
    id: 'pd-004',
    patientId: 'SYN-007',
    visit: 3,
    type: 'Minor',
    description: 'Missed visit 3 window by 2 days',
    detection: 'Self-Reported',
    correctiveAction: 'Pending',
    dataImpact: 'None',
  },
];

// group by visit
const deviationsByVisit = deviationMonitorData.reduce<Record<number, DeviationMonitorRow[]>>(
  (acc, d) => {
    if (!acc[d.visit]) acc[d.visit] = [];
    acc[d.visit].push(d);
    return acc;
  },
  {}
);

export const DeviationMonitorPanel: React.FC = () => {
  const detectionBadge = (d: DetectionMethod) => {
    if (d === 'Monitor-Detected')
      return <span style={{ ...badgeBase, background: '#4299e1', color: '#1a202c' }}>Monitor-Detected</span>;
    return <span style={{ ...badgeBase, background: '#2d3748', color: '#a0aec0' }}>Self-Reported</span>;
  };

  const actionBadge = (a: CorrectiveAction) => {
    const colors: Record<CorrectiveAction, { bg: string; fg: string }> = {
      Pending: { bg: '#ecc94b', fg: '#1a202c' },
      'In Progress': { bg: '#ed8936', fg: '#1a202c' },
      Complete: { bg: '#48bb78', fg: '#1a202c' },
    };
    return <span style={{ ...badgeBase, background: colors[a].bg, color: colors[a].fg }}>{a}</span>;
  };

  const impactBadge = (i: DataIntegrityImpact) => {
    const colors: Record<DataIntegrityImpact, { bg: string; fg: string }> = {
      None: { bg: '#2d3748', fg: '#a0aec0' },
      Minor: { bg: '#ecc94b', fg: '#1a202c' },
      Major: { bg: '#f56565', fg: '#1a202c' },
    };
    return <span style={{ ...badgeBase, background: colors[i].bg, color: colors[i].fg }}>{i}</span>;
  };

  const visitNumbers = Object.keys(deviationsByVisit)
    .map(Number)
    .sort((a, b) => a - b);

  return (
    <div style={cardStyle}>
      <h3 style={headingStyle}>Deviation Monitor Panel</h3>

      {visitNumbers.map((visit) => (
        <div key={visit} style={{ marginBottom: 16 }}>
          <h4 style={{ ...subHeadingStyle, marginBottom: 8 }}>Visit {visit}</h4>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>ID</th>
                <th style={thStyle}>Patient</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Description</th>
                <th style={thStyle}>Detection</th>
                <th style={thStyle}>Corrective Action</th>
                <th style={thStyle}>Data Impact</th>
              </tr>
            </thead>
            <tbody>
              {deviationsByVisit[visit].map((d) => (
                <tr key={d.id}>
                  <td style={tdStyle}>{d.id}</td>
                  <td style={tdStyle}>{d.patientId}</td>
                  <td style={tdStyle}>{d.type}</td>
                  <td style={{ ...tdStyle, maxWidth: 260, whiteSpace: 'normal' }}>{d.description}</td>
                  <td style={tdStyle}>{detectionBadge(d.detection)}</td>
                  <td style={tdStyle}>{actionBadge(d.correctiveAction)}</td>
                  <td style={tdStyle}>{impactBadge(d.dataImpact)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

// ---------- 5. SiteMonitorPanel ----------

interface SiteMonitorData {
  site: string;
  siteLabel: string;
  visitHistory: string[];
  queryResolutionAvg: string;
  outstandingSdv: number;
  recommendedNextVisit: string;
}

const siteMonitorData: SiteMonitorData[] = [
  {
    site: 'UCLA',
    siteLabel: 'UCLA Medical Center',
    visitHistory: ['2026-01-28', '2026-01-14', '2025-12-30'],
    queryResolutionAvg: '1.2d',
    outstandingSdv: 2,
    recommendedNextVisit: '2026-02-04',
  },
  {
    site: 'UCSF',
    siteLabel: 'UCSF Medical Center',
    visitHistory: ['2026-01-25', '2026-01-10', '2025-12-27'],
    queryResolutionAvg: '3.1d',
    outstandingSdv: 5,
    recommendedNextVisit: '2026-02-01',
  },
  {
    site: 'Johns Hopkins',
    siteLabel: 'Johns Hopkins Hospital',
    visitHistory: ['2026-01-27', '2026-01-13', '2025-12-29'],
    queryResolutionAvg: '2.0d',
    outstandingSdv: 3,
    recommendedNextVisit: '2026-02-03',
  },
];

export const SiteMonitorPanel: React.FC = () => {
  const sdvColor = (count: number) => {
    if (count <= 2) return '#48bb78';
    if (count <= 4) return '#ecc94b';
    return '#f56565';
  };

  const resolutionColor = (avg: string) => {
    const val = parseFloat(avg);
    if (val <= 1.5) return '#48bb78';
    if (val <= 2.5) return '#ecc94b';
    return '#f56565';
  };

  return (
    <div style={cardStyle}>
      <h3 style={headingStyle}>Site Monitor Panel</h3>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
        {siteMonitorData.map((site) => (
          <div
            key={site.site}
            style={{
              background: '#0d1117',
              border: '1px solid #2d3748',
              borderRadius: 8,
              padding: 16,
            }}
          >
            <h4 style={{ ...subHeadingStyle, marginBottom: 12 }}>{site.siteLabel}</h4>

            {/* Visit history */}
            <div style={{ marginBottom: 10 }}>
              <div style={{ ...mutedText, marginBottom: 4 }}>Last 3 Monitor Visits</div>
              {site.visitHistory.map((date, i) => (
                <div key={i} style={{ color: '#e2e8f0', fontSize: 13, marginLeft: 8 }}>
                  {date}
                </div>
              ))}
            </div>

            {/* Query resolution */}
            <div style={{ marginBottom: 10, display: 'flex', justifyContent: 'space-between' }}>
              <span style={mutedText}>Avg Query Resolution</span>
              <span style={{ color: resolutionColor(site.queryResolutionAvg), fontSize: 13, fontWeight: 600 }}>
                {site.queryResolutionAvg}
              </span>
            </div>

            {/* Outstanding SDV */}
            <div style={{ marginBottom: 10, display: 'flex', justifyContent: 'space-between' }}>
              <span style={mutedText}>Outstanding SDV Items</span>
              <span style={{ color: sdvColor(site.outstandingSdv), fontSize: 13, fontWeight: 600 }}>
                {site.outstandingSdv}
              </span>
            </div>

            {/* Next visit */}
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={mutedText}>Recommended Next Visit</span>
              <span style={{ color: '#4299e1', fontSize: 13, fontWeight: 600 }}>
                {site.recommendedNextVisit}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
